import type { UserOrganization } from "@shared/types";
import { Form } from "antd";
import { FormModal } from "@/shared/components/FormModal";
import { useCreateDevice, useUpdateDevice } from "../hooks/useDevices";
import { useDevicesStore } from "../store/devices.store";
import type { CreateDeviceRequest, UpdateDeviceRequest } from "../types";
import { DeviceForm } from "./DeviceForm";

interface DeviceFormModalProps {
	organizationId?: string;
	organizations: UserOrganization[];
}

export function DeviceFormModal({ organizationId, organizations }: DeviceFormModalProps) {
	const [form] = Form.useForm<CreateDeviceRequest>();
	const { modalOpen, editingDevice, closeModal } = useDevicesStore();

	const { mutate: createDevice, isPending: isCreating } = useCreateDevice();
	const { mutate: updateDevice, isPending: isUpdating } = useUpdateDevice();

	const handleClose = () => {
		closeModal();
		form.resetFields();
	};

	const handleUpdate = (values: Partial<CreateDeviceRequest>) => {
		if (!editingDevice) {
			return;
		}

		const compareFields: (keyof UpdateDeviceRequest)[] = [
			"name",
			"serialNumber",
			"macAddress",
			"ipAddress",
			"port",
			"direction",
			"locationDescription",
			"username",
			"status",
			"isActive",
		];

		const updateData: UpdateDeviceRequest = {};
		for (const key of compareFields) {
			const newVal = values[key as keyof typeof values];
			const oldVal = editingDevice[key as keyof typeof editingDevice];
			if (newVal !== oldVal && newVal !== undefined) {
				(updateData as Record<string, unknown>)[key] = newVal ?? undefined;
			}
		}
		// password alohida — faqat kiritilganda yuboriladi
		if (values.password) {
			updateData.password = values.password;
		}

		updateDevice({ id: editingDevice.id, data: updateData }, { onSuccess: handleClose });
	};

	const handleSubmit = () => {
		form.validateFields().then((values) => {
			const payload = {
				...values,
				organizationId: values.organizationId ?? organizationId,
			} as CreateDeviceRequest;
			if (editingDevice) {
				handleUpdate(payload);
			} else {
				createDevice(payload, { onSuccess: handleClose });
			}
		});
	};

	return (
		<FormModal
			editing={!!editingDevice}
			title="Yangi qurilma"
			editTitle="Qurilmani tahrirlash"
			open={modalOpen}
			onCancel={handleClose}
			onOk={handleSubmit}
			confirmLoading={isCreating || isUpdating}
			width={720}
		>
			<DeviceForm
				form={form}
				organizationId={editingDevice?.organizationId ?? organizationId}
				organizations={organizations}
				hideOrganization={organizations.length <= 1}
			/>
		</FormModal>
	);
}
